import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Paper,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Select,
  FormControl,
  OutlinedInput,
  SelectChangeEvent,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { COUNTRY_OPTIONS } from '../data/countries';
import ErrorNotification from './ErrorNotification';
import { validateRuleName } from '../graphql';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { MultiSectionDigitalClock } from '@mui/x-date-pickers/MultiSectionDigitalClock';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

// Matching criteria options
interface CriteriaOption {
  id: string;
  label: string;
  description: string;
}

// Split criteria into two groups: 5 in left block, 5 in right block
const leftBlockCriteria: CriteriaOption[] = [
  {
    id: 'qrCodes',
    label: 'QR Codes',
    description: 'Unique scannable codes'
  },
  {
    id: 'styleColor',
    label: 'Style Color Code',
    description: 'Color variant or code of the product'
  },
  {
    id: 'styleCodesNew',
    label: 'Style Codes',
    description: 'Product style codes'
  },
  {
    id: 'country',
    label: 'Country',
    description: 'Country of origin/target'
  },
  {
    id: 'division',
    label: 'Division',
    description: 'Organisational division'
  }
];

const rightBlockCriteria: CriteriaOption[] = [
  {
    id: 'brandName',
    label: 'Brand Name',
    description: 'Official brand name that this rule applies to'
  },
  {
    id: 'athleteFullName',
    label: 'Athlete Full Name',
    description: 'Associated athlete name'
  },
  {
    id: 'gtins',
    label: 'GTINs',
    description: 'Global Trade Item Numbers'
  },
  {
    id: 'category',
    label: 'Category',
    description: 'Product category'
  },
  {
    id: 'boundingBox',
    label: 'Bounding Box',
    description: 'Geographic boundaries'
  }
];

const CreateRule: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editMode = location.state?.mode === 'edit';
  const importMode = location.state?.mode === 'import';
  const importData = location.state?.importData || null;
  const existingRule = location.state?.ruleData || null;
  const forceAppend = location.state?.forceAppend || false;
  const formData = location.state?.formData || null;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [originalValues, setOriginalValues] = useState<any>(null);
  
  const [selectedCriteria, setSelectedCriteria] = useState<string[]>([]);
  
  // Basic Information state
  const [ruleName, setRuleName] = useState<string>('');
  const [ruleNameError, setRuleNameError] = useState<string>('');
  const [ruleNameValidating, setRuleNameValidating] = useState<boolean>(false);
  const [ruleNameValid, setRuleNameValid] = useState<boolean | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string>('');
  const [redirectUrlError, setRedirectUrlError] = useState<string>('');
  const [priority, setPriority] = useState<string>('');

  // Timing & Schedule Modal state
  const [timingModalOpen, setTimingModalOpen] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [startTime, setStartTime] = useState<Dayjs | null>(dayjs());
  const [startTimeOpen, setStartTimeOpen] = useState<boolean>(false);
  const [expirationDate, setExpirationDate] = useState<Dayjs | null>(dayjs().add(30, 'day'));
  const [expirationTime, setExpirationTime] = useState<Dayjs | null>(dayjs().hour(18).minute(29));
  const [expirationTimeOpen, setExpirationTimeOpen] = useState<boolean>(false);
  const [selectedPreset, setSelectedPreset] = useState<string>('30 DAYS');
  const [hasNoExpiration, setHasNoExpiration] = useState<boolean>(false);
  
  // QR Codes input state
  const [qrCodeInput, setQrCodeInput] = useState<string>('');
  const [qrCodeError, setQrCodeError] = useState<string>('');
  const [qrCodes, setQrCodes] = useState<string[]>([]);
  const [qrCodesExpanded, setQrCodesExpanded] = useState<boolean>(true);
  
  // Style Color input state
  const [styleColorInput, setStyleColorInput] = useState<string>('');
  const [styleColorError, setStyleColorError] = useState<string>('');
  const [styleCodes, setStyleCodes] = useState<string[]>([]);
  const [styleColorExpanded, setStyleColorExpanded] = useState<boolean>(true);
  
  // Style Codes input state (new field)
  const [styleCodesNewInput, setStyleCodesNewInput] = useState<string>('');
  const [styleCodesNewErrorMsg, setStyleCodesNewErrorMsg] = useState<string>('');
  const [styleCodesNewList, setStyleCodesNewList] = useState<string[]>([]);
  const [styleCodesNewExpanded, setStyleCodesNewExpanded] = useState<boolean>(true);
  
  // Country state - Multi-select (no input field needed)
  const [countries, setCountries] = useState<string[]>([]);
  const [countryExpanded, setCountryExpanded] = useState<boolean>(true);
  
  // Division input state
  const [divisionInput, setDivisionInput] = useState<string>('');
  const [divisions, setDivisions] = useState<string[]>([]);
  const [divisionExpanded, setDivisionExpanded] = useState<boolean>(true);
  
  // Brand Name input state
  const [brandInput, setBrandInput] = useState<string>('');
  const [brands, setBrands] = useState<string[]>([]);
  const [brandExpanded, setBrandExpanded] = useState<boolean>(true);
  
  // Athlete input state
  const [athleteInput, setAthleteInput] = useState<string>('');
  const [athletes, setAthletes] = useState<string[]>([]);
  const [athleteExpanded, setAthleteExpanded] = useState<boolean>(true);
  
  // GTINs input state
  const [gtinInput, setGtinInput] = useState<string>('');
  const [gtinError, setGtinError] = useState<string>('');
  const [gtins, setGtins] = useState<string[]>([]);
  const [gtinExpanded, setGtinExpanded] = useState<boolean>(true);
  
  // Category input state
  const [categoryInput, setCategoryInput] = useState<string>('');
  const [categories, setCategories] = useState<string[]>([]);
  const [categoryExpanded, setCategoryExpanded] = useState<boolean>(true);
  
  // Bounding Box state
  const [boundingBoxInput, setBoundingBoxInput] = useState<string>('');
  const [boundingBoxes, setBoundingBoxes] = useState<string[]>([]);
  const [boundingBoxExpanded, setBoundingBoxExpanded] = useState<boolean>(true);

  // Notification state
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorNotifMessage, setErrorNotifMessage] = useState('');
  
  // Field validation error states
  const [qrCodesError, setQrCodesError] = useState(false);
  const [styleCodesError, setStyleCodesError] = useState(false);
  const [styleCodesNewError, setStyleCodesNewError] = useState(false);
  const [countriesError, setCountriesError] = useState(false);
  const [divisionsError, setDivisionsError] = useState(false);
  const [brandsError, setBrandsError] = useState(false);
  const [athletesError, setAthletesError] = useState(false);
  const [gtinsError, setGtinsError] = useState(false);
  const [categoriesError, setCategoriesError] = useState(false);
  const [boundingBoxError, setBoundingBoxError] = useState(false);
  const [boundingBoxCoordinates, setBoundingBoxCoordinates] = useState<{
    north: number;
    south: number;
    east: number;
    west: number;
  } | null>(null);
  const [boundingBoxDialogOpen, setBoundingBoxDialogOpen] = useState<boolean>(false); // Modal state for bounding box

  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState<boolean>(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Pre-populate form fields if in edit mode, import mode, or restoring from review page
  useEffect(() => {
    // Priority 0: pre-populate from import data
    if (importMode && importData) {
      setRedirectUrl(importData.redirectUrl || '');

      const criteria: string[] = [];
      if (importData.styleColorCodes?.length) {
        criteria.push('styleColor');
        setStyleCodes(importData.styleColorCodes);
      }
      if (importData.styleCodes?.length) {
        criteria.push('styleCodesNew');
        setStyleCodesNewList(importData.styleCodes);
      }
      setSelectedCriteria(criteria);

      if (importData.startDate) {
        const parsed = dayjs(importData.startDate);
        if (parsed.isValid()) {
          setStartDate(parsed);
        }
      }

      return;
    }

    // First priority: restore from formData (coming back from review)
    if (formData) {

      setRuleName(formData.ruleName || '');
      setRedirectUrl(formData.redirectUrl || '');
      setPriority(formData.priority || '');
      setQrCodes(formData.qrCodes || []);
      setStyleCodes(formData.styleCodes || []);
      setStyleCodesNewList(formData.styleCodesNewList || []);
      setCountries(formData.selectedCountries || []);
      setGtins(formData.gtinCodes || []);
      setBoundingBoxCoordinates(formData.selectedBoundingBox || null);
      setSelectedCriteria(formData.selectedCriteria || []);
      
      // Restore divisions, categories, brand, athlete to arrays (not input fields)
      if (formData.selectedDivision) {
        setDivisions([formData.selectedDivision]);
        setDivisionInput(''); // Clear input
      }
      if (formData.selectedCategory) {
        setCategories([formData.selectedCategory]);
        setCategoryInput(''); // Clear input
      }
      if (formData.brandName) {
        setBrandInput(formData.brandName); // Set directly to input field
      }
      if (formData.athleteFullName) {
        setAthleteInput(formData.athleteFullName); // Set directly to input field
      }
      
      // Restore schedule
      if (formData.schedule) {
        if (formData.schedule.startDate) setStartDate(dayjs(formData.schedule.startDate));
        if (formData.schedule.startTime) setStartTime(dayjs(`2000-01-01T${formData.schedule.startTime}`));
        if (formData.schedule.endDate) setExpirationDate(dayjs(formData.schedule.endDate));
        if (formData.schedule.endTime) setExpirationTime(dayjs(`2000-01-01T${formData.schedule.endTime}`));
      }
      
      return; // Exit early, don't run the edit mode logic below
    }
    
    // Second priority: pre-populate from existing rule (initial edit)
    if (editMode && existingRule) {

      // Store original values for change tracking
      setOriginalValues({
        name: existingRule.name,
        redirectUrl: existingRule.redirectUrl,
        priority: existingRule.priority,
        qrs: existingRule.qrs,
        styleColorCodes: existingRule.styleColorCodes,
        countries: existingRule.countries,
        divisions: existingRule.divisions,
        brandName: existingRule.brandName,
        athleteFullName: existingRule.athleteFullName,
        gtins: existingRule.gtins,
        categories: existingRule.categories,
        boundingBox: existingRule.boundingBox,
        ruleStartFrom: existingRule.ruleStartFrom,
        ruleEndAt: existingRule.ruleEndAt
      });
      
      // Basic information
      setRuleName(existingRule.name || '');
      setRedirectUrl(existingRule.redirectUrl || '');
      setPriority(
        existingRule.priority === 124 ? 'critical' :
        existingRule.priority === 110 ? 'high' : 
        existingRule.priority === 100 ? 'medium' : 
        existingRule.priority === 80 ? 'low' :
        'medium'  // default fallback
      );
      
      // Pre-populate criteria based on what fields have values
      const criteria: string[] = [];
      if (existingRule.qrs?.length) {
        criteria.push('qrCodes');
        setQrCodes(existingRule.qrs);
      }
      if (existingRule.styleColorCodes?.length) {
        criteria.push('styleColor');
        setStyleCodes(existingRule.styleColorCodes);
      }
      if (existingRule.styleCodes?.length) {
        criteria.push('styleCodesNew');
        setStyleCodesNewList(existingRule.styleCodes);
      }
      if (existingRule.countries?.length) {
        criteria.push('country');
        setCountries(existingRule.countries);
      }
      if (existingRule.divisions?.length) {
        criteria.push('division');
        // Add to divisions array, not just input
        setDivisions(existingRule.divisions);
      }
      if (existingRule.brandName) {
        criteria.push('brandName');
        // Set directly to input field (single value)
        setBrandInput(existingRule.brandName);
      }
      if (existingRule.athleteFullName) {
        criteria.push('athleteFullName');
        // Set directly to input field (single value)
        setAthleteInput(existingRule.athleteFullName);
      }
      if (existingRule.gtins?.length) {
        criteria.push('gtins');
        setGtins(existingRule.gtins);
      }
      if (existingRule.categories?.length) {
        criteria.push('category');
        // Add to categories array, not just input
        setCategories(existingRule.categories);
      }
      if (existingRule.boundingBox) {
        criteria.push('boundingBox');
        setBoundingBoxCoordinates(existingRule.boundingBox);
      }
      
      setSelectedCriteria(criteria);
      
      // Schedule information
      if (existingRule.ruleStartFrom) {
        setStartDate(dayjs(existingRule.ruleStartFrom));
        setStartTime(dayjs(existingRule.ruleStartFrom));
      }
      if (existingRule.ruleEndAt) {
        setExpirationDate(dayjs(existingRule.ruleEndAt));
        setExpirationTime(dayjs(existingRule.ruleEndAt));
      }
    }
  }, [editMode, existingRule, formData, importMode, importData]);

  // Track any form changes to mark as unsaved
  useEffect(() => {
    // Don't mark as changed during initial load or when coming back from review
    if (editMode || formData) {
      return;
    }
    
    // Check if any field has been modified
    const hasChanges = 
      ruleName.trim() !== '' ||
      redirectUrl.trim() !== '' ||
      priority.trim() !== '' ||
      selectedCriteria.length > 0 ||
      qrCodes.length > 0 ||
      styleCodes.length > 0 ||
      countries.length > 0 ||
      divisions.length > 0 ||
      brands.length > 0 ||
      athletes.length > 0 ||
      gtins.length > 0 ||
      categories.length > 0 ||
      boundingBoxCoordinates !== null ||
      brandInput.trim() !== '' ||
      athleteInput.trim() !== '';
    
    setHasUnsavedChanges(hasChanges);
  }, [
    ruleName, redirectUrl, priority, selectedCriteria, qrCodes, styleCodes,
    countries, divisions, brands, athletes, gtins, categories, boundingBoxCoordinates,
    brandInput, athleteInput, editMode, formData
  ]);

  // Handle browser navigation (back button, refresh, close tab)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && !isSubmitting) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, isSubmitting]);

  // Add QR Code with Crockford Base32 validation (13 chars, A-Z0-9 excluding I, L, O, U)
  const handleAddQrCode = () => {
    const input = qrCodeInput.trim();
    
    // Clear previous error
    setQrCodeError('');
    
    if (!input) {
      return;
    }
    
    // Validation 0: Check for spaces FIRST (before length check for better error message)
    if (/\s/.test(input)) {
      setQrCodeError('QR code cannot contain spaces. Remove all spaces and enter as one continuous code.');
      return;
    }
    
    // Validation 0b: Only ASCII characters allowed (no unicode)
    // eslint-disable-next-line no-control-regex
    if (!/^[\x00-\x7F]*$/.test(input)) {
      setQrCodeError('QR code contains invalid characters (only standard ASCII allowed)');
      return;
    }
    
    // Validation 1: Must be exactly 13 characters (with lowercase detection)
    if (input.length !== 13) {
      const hasLowercase = input !== input.toUpperCase();
      const caseInfo = hasLowercase ? ' lowercase' : '';
      setQrCodeError(`QR code must be exactly 13 UPPERCASE characters. "${input}" has ${input.length}${caseInfo} characters.`);
      return;
    }
    
    // Validation 2: Must be uppercase only
    if (input !== input.toUpperCase()) {
      setQrCodeError('QR code must contain only UPPERCASE characters');
      return;
    }
    
    // Validation 3: Must be valid Crockford Base32 (A-Z, 0-9, excluding I, L, O, U)
    // This matches the validation used by sensors-qr-redirect — no prefix restriction.
    const crockfordPattern = /^((?![ILOU])[A-Z0-9]){13}$/;
    if (!crockfordPattern.test(input)) {
      const invalidChars = input.split('').filter(c => /[ILOU]/.test(c) || !/[A-Z0-9]/.test(c));
      const uniqueInvalid = Array.from(new Set(invalidChars));
      setQrCodeError(`QR code contains invalid character(s): ${uniqueInvalid.map(c => `"${c}"`).join(', ')}. Valid characters are A-Z and 0-9, excluding I, L, O, U.`);
      return;
    }
    
    // Validation 5: Check for duplicates
    if (qrCodes.includes(input)) {
      setQrCodeError(`QR code "${input}" has already been added`);
      return;
    }
    
    // All validations passed - Add the QR code
    setQrCodes([...qrCodes, input]);
    setQrCodeInput('');
    setQrCodeError(''); // Clear error on success
  };

  // Remove QR Code
  const handleRemoveQrCode = (index: number) => {
    setQrCodes(qrCodes.filter((_, i) => i !== index));
  };

  // Style Color handlers with LLNNNN-NNN or NNNNNN-NNN format validation
  const handleAddStyleColor = () => {
    const input = styleColorInput.trim();
    
    // Clear previous error
    setStyleColorError('');
    
    if (!input) {
      return;
    }
    
    // Validation 0: Check for spaces FIRST (better user message)
    if (/\s/.test(input)) {
      setStyleColorError('Style code cannot contain spaces. Remove all spaces and enter as one continuous code.');
      return;
    }
    
    // Validation 0b: Only ASCII characters allowed
    // eslint-disable-next-line no-control-regex
    if (!/^[\x00-\x7F]*$/.test(input)) {
      setStyleColorError('Style code contains invalid characters (only standard ASCII allowed)');
      return;
    }
    
    // Validation 0c: Check for wrong hyphen types
    if (input.includes('–') || input.includes('—') || input.includes('_')) {
      setStyleColorError('Use a regular hyphen (-), not en-dash, em-dash, or underscore');
      return;
    }
    
    // Validation 0d: Must have exactly 1 hyphen
    const hyphenCount = (input.match(/-/g) || []).length;
    if (hyphenCount !== 1) {
      setStyleColorError(`Style code must have exactly 1 hyphen. Found ${hyphenCount}.`);
      return;
    }
    
    // Validation 1: Must be exactly 10 characters (including hyphen)
    if (input.length !== 10) {
      setStyleColorError(`Style code must be exactly 10 characters. "${input}" has ${input.length} characters.`);
      return;
    }
    
    // Validation 2: Check for uppercase letters (if any letters exist)
    const hasLetters = /[a-zA-Z]/.test(input);
    if (hasLetters && input !== input.toUpperCase()) {
      setStyleColorError('Style code must contain only UPPERCASE letters');
      return;
    }
    
    // Validation 3: Match exact pattern LLNNNN-NNN or NNNNNN-NNN
    // Format 1: LL (2 letters) + NNNN (4 numbers) + - + NNN (3 numbers)
    // Format 2: NNNNNN (6 numbers) + - + NNN (3 numbers)
    
    const pattern = /^([A-Z]{2}[0-9]{4}|[0-9]{6})-[0-9]{3}$/;
    
    if (!pattern.test(input)) {
      // Detailed error showing which format is expected
      const chars = input.split('');
      const beforeHyphen = input.split('-')[0];
      const afterHyphen = input.split('-')[1];
      
      let errorMsg = 'Style code must follow pattern LLNNNN-NNN or NNNNNN-NNN. ';
      
      // Check if format could be letters+numbers (LLNNNN-NNN)
      if (beforeHyphen.length === 6) {
        const hasLetters = /[A-Z]/.test(beforeHyphen);
        const hasNumbers = /[0-9]/.test(beforeHyphen);
        
        if (hasLetters && hasNumbers) {
          // Mixed format - check LLNNNN pattern
          if (chars[0] && !/[A-Z]/.test(chars[0])) errorMsg += `Position 1 must be a letter (got "${chars[0]}"). `;
          else if (chars[1] && !/[A-Z]/.test(chars[1])) errorMsg += `Position 2 must be a letter (got "${chars[1]}"). `;
          else if (chars[2] && !/[0-9]/.test(chars[2])) errorMsg += `Position 3 must be a number (got "${chars[2]}"). `;
          else if (chars[3] && !/[0-9]/.test(chars[3])) errorMsg += `Position 4 must be a number (got "${chars[3]}"). `;
          else if (chars[4] && !/[0-9]/.test(chars[4])) errorMsg += `Position 5 must be a number (got "${chars[4]}"). `;
          else if (chars[5] && !/[0-9]/.test(chars[5])) errorMsg += `Position 6 must be a number (got "${chars[5]}"). `;
        } else if (!hasLetters) {
          // All numbers before hyphen - check if after hyphen is correct
          if (!afterHyphen || afterHyphen.length !== 3 || !/^[0-9]{3}$/.test(afterHyphen)) {
            errorMsg += `Last 3 characters after hyphen must be numbers. `;
          }
        }
      }
      
      // Check hyphen position
      if (chars[6] && chars[6] !== '-') errorMsg += `Position 7 must be a hyphen "-" (got "${chars[6]}"). `;
      
      // Check last 3 digits
      if (chars[7] && !/[0-9]/.test(chars[7])) errorMsg += `Position 8 must be a number (got "${chars[7]}"). `;
      if (chars[8] && !/[0-9]/.test(chars[8])) errorMsg += `Position 9 must be a number (got "${chars[8]}"). `;
      if (chars[9] && !/[0-9]/.test(chars[9])) errorMsg += `Position 10 must be a number (got "${chars[9]}"). `;
      
      setStyleColorError(errorMsg.trim());
      return;
    }
    
    // Validation 4: Check for duplicates
    if (styleCodes.includes(input)) {
      setStyleColorError(`Style code "${input}" has already been added`);
      return;
    }
    
    // All validations passed - Add the style code
    setStyleCodes([...styleCodes, input]);
    setStyleColorInput('');
    setStyleColorError(''); // Clear error on success
  };
  const handleRemoveStyleColor = (index: number) => {
    setStyleCodes(styleCodes.filter((_, i) => i !== index));
  };

  // Style Codes (New) handlers with LLNNNN or NNNNNN format validation
  const handleAddStyleCodesNew = () => {
    const input = styleCodesNewInput.trim();
    
    // Clear previous error
    setStyleCodesNewErrorMsg('');
    
    if (!input) {
      return;
    }
    
    // Validation 0: Check for spaces FIRST
    if (/\s/.test(input)) {
      setStyleCodesNewErrorMsg('Style code cannot contain spaces. Remove all spaces and enter as one continuous code.');
      return;
    }
    
    // Validation 0b: Only ASCII characters allowed
    // eslint-disable-next-line no-control-regex
    if (!/^[\x00-\x7F]*$/.test(input)) {
      setStyleCodesNewErrorMsg('Style code contains invalid characters (only standard ASCII allowed)');
      return;
    }
    
    // Validation 1: Must be exactly 6 characters
    if (input.length !== 6) {
      setStyleCodesNewErrorMsg(`Style code must be exactly 6 characters. "${input}" has ${input.length} characters.`);
      return;
    }
    
    // Validation 2: Check for uppercase letters (if any letters exist)
    const hasLetters = /[a-zA-Z]/.test(input);
    if (hasLetters && input !== input.toUpperCase()) {
      setStyleCodesNewErrorMsg('Style code must contain only UPPERCASE letters');
      return;
    }
    
    // Validation 3: Match exact pattern LLNNNN or NNNNNN
    // Format 1: LL (2 letters) + NNNN (4 numbers)
    // Format 2: NNNNNN (6 numbers)
    
    const pattern = /^([A-Z]{2}[0-9]{4}|[0-9]{6})$/;
    
    if (!pattern.test(input)) {
      // Detailed error showing which format is expected
      const chars = input.split('');
      let errorMsg = 'Style code must follow pattern LLNNNN or NNNNNN. ';
      
      const hasLetters = /[A-Z]/.test(input);
      const hasNumbers = /[0-9]/.test(input);
      
      if (hasLetters && hasNumbers) {
        // Mixed format - check LLNNNN pattern
        if (chars[0] && !/[A-Z]/.test(chars[0])) errorMsg += `Position 1 must be a letter (got "${chars[0]}"). `;
        else if (chars[1] && !/[A-Z]/.test(chars[1])) errorMsg += `Position 2 must be a letter (got "${chars[1]}"). `;
        else if (chars[2] && !/[0-9]/.test(chars[2])) errorMsg += `Position 3 must be a number (got "${chars[2]}"). `;
        else if (chars[3] && !/[0-9]/.test(chars[3])) errorMsg += `Position 4 must be a number (got "${chars[3]}"). `;
        else if (chars[4] && !/[0-9]/.test(chars[4])) errorMsg += `Position 5 must be a number (got "${chars[4]}"). `;
        else if (chars[5] && !/[0-9]/.test(chars[5])) errorMsg += `Position 6 must be a number (got "${chars[5]}"). `;
      } else if (hasLetters && !hasNumbers) {
        errorMsg += 'Cannot be all letters. Must be LLNNNN (2 letters + 4 numbers) or NNNNNN (6 numbers).';
      } else {
        errorMsg += 'Invalid format.';
      }
      
      setStyleCodesNewErrorMsg(errorMsg.trim());
      return;
    }
    
    // Validation 4: Check for duplicates
    if (styleCodesNewList.includes(input)) {
      setStyleCodesNewErrorMsg(`Style code "${input}" has already been added`);
      return;
    }
    
    // All validations passed - Add the style code
    setStyleCodesNewList([...styleCodesNewList, input]);
    setStyleCodesNewInput('');
    setStyleCodesNewErrorMsg('');
  };
  
  const handleRemoveStyleCodesNew = (index: number) => {
    setStyleCodesNewList(styleCodesNewList.filter((_, i) => i !== index));
  };

  // Country handlers - Multi-select
  const handleCountryChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setCountries(typeof value === 'string' ? value.split(',') : value);
  };
  const handleRemoveCountry = (countryCode: string) => {
    setCountries(countries.filter((c) => c !== countryCode));
  };

  // Division handlers
  const handleAddDivision = () => {
    if (divisionInput.trim()) {
      setDivisions([...divisions, divisionInput.trim()]);
      setDivisionInput('');
    }
  };
  const handleRemoveDivision = (index: number) => {
    setDivisions(divisions.filter((_, i) => i !== index));
  };

  // Brand Name handlers
  const handleAddBrand = () => {
    if (brandInput.trim()) {
      setBrands([...brands, brandInput.trim()]);
      setBrandInput('');
    }
  };
  const handleRemoveBrand = (index: number) => {
    setBrands(brands.filter((_, i) => i !== index));
  };

  // Athlete handlers
  const handleAddAthlete = () => {
    if (athleteInput.trim()) {
      setAthletes([...athletes, athleteInput.trim()]);
      setAthleteInput('');
    }
  };
  const handleRemoveAthlete = (index: number) => {
    setAthletes(athletes.filter((_, i) => i !== index));
  };

  // GTIN handlers with validation: Exactly 14 digits starting with 00
  // Validate rule name (check if already exists)
  const handleValidateRuleName = useCallback(async (name: string) => {
    // Skip validation if name is empty or in edit mode
    if (!name.trim() || editMode) {
      setRuleNameError('');
      setRuleNameValid(null);
      return;
    }

    setRuleNameValidating(true);
    setRuleNameError('');
    
    try {
      const result = await validateRuleName(name.trim());
      
      if (result.isValid) {
        setRuleNameValid(true);
        setRuleNameError('');
      } else {
        setRuleNameValid(false);
        setRuleNameError(result.message || 'Rule name already exists');
      }
    } catch (error: any) {
      console.error('Error validating rule name:', error);
      // Don't show error to user - silently fail and let them proceed
      // The backend will catch it on submit if name actually exists
      setRuleNameValid(null);
      setRuleNameError('');
    } finally {
      setRuleNameValidating(false);
    }
  }, [editMode]);

  // Debounced rule name validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (ruleName) {
        handleValidateRuleName(ruleName);
      }
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [ruleName, handleValidateRuleName]);

  const handleAddGtin = () => {
    const input = gtinInput.trim();
    
    // Clear previous error
    setGtinError('');
    
    if (!input) {
      return;
    }
    
    // Validation 0: Check for spaces FIRST
    if (/\s/.test(input)) {
      setGtinError('GTIN cannot contain spaces. Remove all spaces and enter as one continuous number.');
      return;
    }
    
    // Validation 1: Must be numeric only (no letters or special characters)
    if (!/^[0-9]+$/.test(input)) {
      setGtinError('GTIN must contain only numbers (0-9). No letters or special characters allowed.');
      return;
    }
    
    // Validation 2: Must be exactly 14 digits
    if (input.length !== 14) {
      setGtinError(`GTIN must be exactly 14 digits. "${input}" has ${input.length} digits.`);
      return;
    }
    
    // Validation 3: Must start with "00" (two zeros)
    if (!input.startsWith('00')) {
      setGtinError('GTIN must start with "00" (two zeros)');
      return;
    }
    
    // Validation 4: Match full pattern (00 + 12 more digits)
    if (!/^00[0-9]{12}$/.test(input)) {
      setGtinError('GTIN must follow format: 00 + 12 digits (e.g., 00195866721227)');
      return;
    }
    
    // Validation 5: Check for duplicates
    if (gtins.includes(input)) {
      setGtinError(`GTIN "${input}" has already been added`);
      return;
    }
    
    // All validations passed - Add the GTIN
    setGtins([...gtins, input]);
    setGtinInput('');
    setGtinError(''); // Clear error on success
  };
  const handleRemoveGtin = (index: number) => {
    setGtins(gtins.filter((_, i) => i !== index));
  };

  // Category handlers
  const handleAddCategory = () => {
    if (categoryInput.trim()) {
      setCategories([...categories, categoryInput.trim()]);
      setCategoryInput('');
    }
  };
  const handleRemoveCategory = (index: number) => {
    setCategories(categories.filter((_, i) => i !== index));
  };

  // Timing & Schedule handlers
  const handlePresetClick = (preset: string) => {
    setSelectedPreset(preset);
    const now = dayjs();
    setStartDate(now);
    setStartTime(now);
    
    let newExpiration = now;
    switch (preset) {
      case '7 DAYS':
        newExpiration = now.add(7, 'day');
        break;
      case '30 DAYS':
        newExpiration = now.add(30, 'day');
        break;
      case '90 DAYS':
        newExpiration = now.add(90, 'day');
        break;
      case '6 MONTHS':
        newExpiration = now.add(6, 'month');
        break;
      case '1 YEAR':
        newExpiration = now.add(1, 'year');
        break;
    }
    setExpirationDate(newExpiration);
    setExpirationTime(newExpiration);
  };

  // Calculate rule duration in days
  const calculateDuration = () => {
    if (!startDate || !expirationDate) return 0;
    return expirationDate.diff(startDate, 'day');
  };

  // Calculate activation status
  const getActivationStatus = () => {
    if (!startDate) return { text: 'Not set', color: '#666' };
    const now = dayjs();
    if (startDate.isBefore(now)) {
      return { text: 'Already active', color: '#4CAF50' };
    } else {
      const daysUntil = startDate.diff(now, 'day');
      return { text: `Starts in ${daysUntil} days`, color: '#FF9800' };
    }
  };

  // Calculate expiration status
  const getExpirationStatus = () => {
    if (!expirationDate) return { text: 'Not set', color: '#666' };
    const now = dayjs();
    const daysUntil = expirationDate.diff(now, 'day');
    if (daysUntil < 0) {
      return { text: 'Expired', color: '#F44336' };
    }
    return { text: `In ${daysUntil} days`, color: '#FF9800' };
  };

  // Bounding Box handlers
  const handleAddBoundingBox = () => {
    if (boundingBoxInput.trim()) {
      setBoundingBoxes([...boundingBoxes, boundingBoxInput.trim()]);
      setBoundingBoxInput('');
    }
  };
  const handleRemoveBoundingBox = (index: number) => {
    setBoundingBoxes(boundingBoxes.filter((_, i) => i !== index));
  };

  // Handle checkbox change
  const handleCheckboxChange = (criteriaId: string) => {
    setSelectedCriteria(prev => {
      if (prev.includes(criteriaId)) {
        if (criteriaId === 'qrCodes') setQrCodes([]);
        if (criteriaId === 'styleColor') setStyleCodes([]);
        if (criteriaId === 'styleCodesNew') setStyleCodesNewList([]);
        if (criteriaId === 'country') setCountries([]);
        if (criteriaId === 'division') setDivisions([]);
        if (criteriaId === 'brandName') setBrandInput('');
        if (criteriaId === 'athleteFullName') setAthleteInput('');
        if (criteriaId === 'gtins') setGtins([]);
        if (criteriaId === 'category') setCategories([]);
        if (criteriaId === 'boundingBox') setBoundingBoxCoordinates(null);
        return prev.filter(id => id !== criteriaId);
      } else {
        // When checking, auto-expand
        if (criteriaId === 'qrCodes') setQrCodesExpanded(true);
        if (criteriaId === 'styleColor') setStyleColorExpanded(true);
        if (criteriaId === 'country') setCountryExpanded(true);
        if (criteriaId === 'division') setDivisionExpanded(true);
        if (criteriaId === 'brandName') setBrandExpanded(true);
        if (criteriaId === 'athleteFullName') setAthleteExpanded(true);
        if (criteriaId === 'gtins') setGtinExpanded(true);
        if (criteriaId === 'category') setCategoryExpanded(true);
        if (criteriaId === 'boundingBox') setBoundingBoxExpanded(true);
        return [...prev, criteriaId];
      }
    });
  };

  // Toggle expand/collapse functions
  const toggleQrCodesExpand = () => setQrCodesExpanded(!qrCodesExpanded);
  const toggleStyleColorExpand = () => setStyleColorExpanded(!styleColorExpanded);
  const toggleStyleCodesNewExpand = () => setStyleCodesNewExpanded(!styleCodesNewExpanded);
  const toggleCountryExpand = () => setCountryExpanded(!countryExpanded);
  const toggleDivisionExpand = () => setDivisionExpanded(!divisionExpanded);
  const toggleBrandExpand = () => setBrandExpanded(!brandExpanded);
  const toggleAthleteExpand = () => setAthleteExpanded(!athleteExpanded);
  const toggleGtinExpand = () => setGtinExpanded(!gtinExpanded);
  const toggleCategoryExpand = () => setCategoryExpanded(!categoryExpanded);
  const toggleBoundingBoxExpand = () => setBoundingBoxExpanded(!boundingBoxExpanded);

  // Comprehensive URL validation with all edge cases
  const validateRedirectUrl = (url: string): boolean => {
    // Reset error
    setRedirectUrlError('');
    
    // Check if empty
    if (!url || url.trim() === '') {
      setRedirectUrlError('Redirect URL is required');
      return false;
    }
    
    // Trim whitespace
    url = url.trim();
    
    // Check for spaces
    if (url.includes(' ')) {
      setRedirectUrlError('URL cannot contain spaces');
      return false;
    }
    
    // Convert to lowercase for protocol check
    const lowerUrl = url.toLowerCase();
    
    // Check if starts with https://
    if (!lowerUrl.startsWith('https://')) {
      if (lowerUrl.startsWith('http://')) {
        setRedirectUrlError('Only HTTPS URLs are allowed. Please use https:// instead of http://');
      } else {
        setRedirectUrlError('URL must start with https://');
      }
      return false;
    }
    
    // Check URL length (max 2000 characters)
    if (url.length > 2000) {
      setRedirectUrlError('URL is too long (maximum 2000 characters)');
      return false;
    }
    
    // Try to parse as URL and validate against whitelist
    try {
      const urlObj = new URL(url);
      
      // Whitelist validation - URL origin must be from an allowed domain
      const allowedExactOrigins = [
        'https://www.nike.com',
        'https://www.nikestore.com.cn',
      ];
      // Wildcard domains: any subdomain of these is allowed (HTTPS only)
      const allowedWildcardDomains = [
        'nikecloud.com',
      ];

      const isAllowedOrigin =
        allowedExactOrigins.includes(urlObj.origin) ||
        allowedWildcardDomains.some(
          (domain) =>
            urlObj.protocol === 'https:' &&
            (urlObj.hostname === domain || urlObj.hostname.endsWith('.' + domain))
        );

      if (!isAllowedOrigin) {
        setRedirectUrlError(
          'Redirect URL must be from an allowed domain: www.nike.com, www.nikestore.com.cn, or *.nikecloud.com'
        );
        return false;
      }

      // Success!
      return true;
    } catch (e) {
      // If URL parsing fails, show the whitelist error directly
      setRedirectUrlError(
        'Redirect URL must be from an allowed domain: www.nike.com, www.nikestore.com.cn, or *.nikecloud.com'
      );
      return false;
    }
  };

  // Handle Next button - Navigate directly to Step 3 (Review), skipping Step 2
  const handleNext = () => {
    // Clear all previous error states
    setQrCodesError(false);
    setStyleCodesError(false);
    setCountriesError(false);
    setDivisionsError(false);
    setBrandsError(false);
    setAthletesError(false);
    setGtinsError(false);
    setCategoriesError(false);
    setBoundingBoxError(false);
    
    // Validate rule name
    if (!ruleName || ruleName.trim() === '') {
      setErrorNotifMessage('Please enter a rule name');
      setShowErrorNotification(true);
      return;
    }
    
    // Check if rule name already exists (only for new rules, not edits)
    if (!editMode && ruleNameValid === false) {
      setErrorNotifMessage(ruleNameError || 'This rule name already exists');
      setShowErrorNotification(true);
      return;
    }
    
    // Validate redirect URL
    if (!validateRedirectUrl(redirectUrl)) {
      setErrorNotifMessage(redirectUrlError || 'Please enter a valid redirect URL');
      setShowErrorNotification(true);
      return;
    }
    
    // Validate priority
    if (!priority) {
      setErrorNotifMessage('Please select a priority');
      setShowErrorNotification(true);
      return;
    }
    
    // Validate criteria
    if (selectedCriteria.length === 0) {
      setErrorNotifMessage('Please select at least one matching criteria');
      setShowErrorNotification(true);
      return;
    }
    
    // Validate that selected criteria have values
    if (selectedCriteria.includes('qrCodes') && qrCodes.length === 0) {
      setQrCodesError(true);
      setErrorNotifMessage('Please enter at least one QR Code');
      setShowErrorNotification(true);
      return;
    }
    
    if (selectedCriteria.includes('styleColor') && styleCodes.length === 0) {
      setStyleCodesError(true);
      setErrorNotifMessage('Please enter at least one Style Color Code');
      setShowErrorNotification(true);
      return;
    }
    
    if (selectedCriteria.includes('styleCodesNew') && styleCodesNewList.length === 0) {
      setStyleCodesNewError(true);
      setErrorNotifMessage('Please enter at least one Style Code');
      setShowErrorNotification(true);
      return;
    }
    
    if (selectedCriteria.includes('country') && countries.length === 0) {
      setCountriesError(true);
      setErrorNotifMessage('Please select at least one Country');
      setShowErrorNotification(true);
      return;
    }
    
    if (selectedCriteria.includes('division') && divisions.length === 0) {
      setDivisionsError(true);
      setErrorNotifMessage('Please select a Division');
      setShowErrorNotification(true);
      return;
    }
    
    if (selectedCriteria.includes('brandName') && !brandInput.trim()) {
      setBrandsError(true);
      setErrorNotifMessage('Please enter a Brand Name');
      setShowErrorNotification(true);
      return;
    }
    
    if (selectedCriteria.includes('athleteFullName') && !athleteInput.trim()) {
      setAthletesError(true);
      setErrorNotifMessage('Please enter an Athlete Full Name');
      setShowErrorNotification(true);
      return;
    }
    
    if (selectedCriteria.includes('gtins') && gtins.length === 0) {
      setGtinsError(true);
      setErrorNotifMessage('Please enter at least one GTIN');
      setShowErrorNotification(true);
      return;
    }
    
    if (selectedCriteria.includes('category') && categories.length === 0) {
      setCategoriesError(true);
      setErrorNotifMessage('Please select a Category');
      setShowErrorNotification(true);
      return;
    }
    
    if (selectedCriteria.includes('boundingBox') && !boundingBoxCoordinates) {
      setBoundingBoxError(true);
      setErrorNotifMessage('Please select a location for Bounding Box');
      setShowErrorNotification(true);
      return;
    }

    // Prepare complete payload for Review page
    setIsSubmitting(true); // Mark as submitting to bypass unsaved changes warning
    setHasUnsavedChanges(false); // Clear unsaved flag
    navigate('/review-rule', {
      state: {
        editMode,
        ruleId: existingRule?.ruleId,
        forceAppend,
        originalRule: existingRule,
        ruleName,
        redirectUrl,
        priority,
        selectedCriteria,
        qrCodes,
        styleCodes,
        styleCodesNewList,
        selectedCountries: countries,
        selectedDivision: divisions.length > 0 ? divisions[0] : '',
        selectedCategory: categories.length > 0 ? categories[0] : '',
        brandName: brandInput.trim() || '',
        athleteFullName: athleteInput.trim() || '',
        gtinCodes: gtins,
        selectedBoundingBox: boundingBoxCoordinates,
        schedule: {
          startDate: startDate ? startDate.format('YYYY-MM-DD') : '',
          startTime: startTime ? startTime.format('HH:mm') : '',
          endDate: expirationDate ? expirationDate.format('YYYY-MM-DD') : '',
          endTime: expirationTime ? expirationTime.format('HH:mm') : '',
          durationDays: startDate && expirationDate ? expirationDate.diff(startDate, 'day') : null
        }
      }
    });
  };

  // Handle Back button
  const handleBack = () => {
    if (hasUnsavedChanges && !isSubmitting) {
      setPendingNavigation('/');
      setUnsavedChangesDialogOpen(true);
    } else {
      navigate('/', { state: { view: 'my-rules' } });
    }
  };

  // Helper function to get expand state and toggle function for each criteria
  const getCriteriaConfig = (criteriaId: string) => {
    const configs: Record<string, any> = {
      qrCodes: { expanded: qrCodesExpanded, toggle: toggleQrCodesExpand, input: qrCodeInput, setInput: (val: string) => { setQrCodeInput(val); setQrCodeError(''); setQrCodesError(false); }, items: qrCodes, handleAdd: handleAddQrCode, handleRemove: handleRemoveQrCode, placeholder: "Add QR codes if needed...", description: "Add QR codes as 13-character Crockford Base32 IDs (e.g., 04GYCQZSVBSWP, 000MDG6DX43V4). Valid characters: A-Z and 0-9, excluding I, L, O, U.", error: qrCodeError || (qrCodesError && qrCodes.length === 0 ? 'Required field - please add at least one QR code' : '') },
      styleColor: { expanded: styleColorExpanded, toggle: toggleStyleColorExpand, input: styleColorInput, setInput: (val: string) => { setStyleColorInput(val); setStyleColorError(''); setStyleCodesError(false); }, items: styleCodes, handleAdd: handleAddStyleColor, handleRemove: handleRemoveStyleColor, placeholder: "Add style color codes if needed...", description: "Add style color codes following format: LLNNNN-NNN or NNNNNN-NNN (e.g., FB1234-001, 951234-001). L=letter, N=number.", error: styleColorError || (styleCodesError && styleCodes.length === 0 ? 'Required field - please add at least one style color code' : '') },
      styleCodesNew: { expanded: styleCodesNewExpanded, toggle: toggleStyleCodesNewExpand, input: styleCodesNewInput, setInput: (val: string) => { setStyleCodesNewInput(val); setStyleCodesNewErrorMsg(''); setStyleCodesNewError(false); }, items: styleCodesNewList, handleAdd: handleAddStyleCodesNew, handleRemove: handleRemoveStyleCodesNew, placeholder: "Add style codes...", description: "Enter product style codes following format: LLNNNN or NNNNNN (e.g., FB1234, 951234). L=letter, N=number.", error: styleCodesNewErrorMsg || (styleCodesNewError && styleCodesNewList.length === 0 ? 'Required field - please add at least one style code' : '') },
      country: { expanded: countryExpanded, toggle: toggleCountryExpand, items: countries, handleChange: (e: SelectChangeEvent<typeof countries>) => { handleCountryChange(e); setCountriesError(false); }, handleRemove: handleRemoveCountry, placeholder: "Select countries", description: "Choose one or more countries. The list is alphabetical with ISO codes for clarity.", helperText: "You can pick multiple countries", useDropdown: true, headingColor: '#000', error: countriesError && countries.length === 0 },
      division: { expanded: divisionExpanded, toggle: toggleDivisionExpand, input: divisionInput, setInput: (val: string) => { setDivisionInput(val); setDivisionsError(false); }, items: divisions, handleAdd: handleAddDivision, handleRemove: handleRemoveDivision, placeholder: "Add divisions...", description: "Organisational division.", error: divisionsError && divisions.length === 0 ? 'Required field - please add a division' : '' },
      brandName: { expanded: brandExpanded, toggle: toggleBrandExpand, input: brandInput, setInput: (val: string) => { setBrandInput(val); setBrandsError(false); }, items: brands, handleAdd: handleAddBrand, handleRemove: handleRemoveBrand, placeholder: "Enter brand name...", description: "Official brand name that this rule applies to.", error: brandsError && !brandInput.trim() ? 'Required field - please enter a brand name' : '' },
      athleteFullName: { expanded: athleteExpanded, toggle: toggleAthleteExpand, input: athleteInput, setInput: (val: string) => { setAthleteInput(val); setAthletesError(false); }, items: athletes, handleAdd: handleAddAthlete, handleRemove: handleRemoveAthlete, placeholder: "Enter athlete name...", description: "Associated athlete name.", error: athletesError && !athleteInput.trim() ? 'Required field - please enter an athlete name' : '' },
      gtins: { expanded: gtinExpanded, toggle: toggleGtinExpand, input: gtinInput, setInput: (val: string) => { setGtinInput(val); setGtinError(''); setGtinsError(false); }, items: gtins, handleAdd: handleAddGtin, handleRemove: handleRemoveGtin, placeholder: "Add GTINs if needed...", description: "Add GTIN codes (exactly 14 digits starting with 00). Example: 00195866721227.", error: gtinError || (gtinsError && gtins.length === 0 ? 'Required field - please add at least one GTIN' : ''), helperText: "Enter exactly 14 digits starting with 00", headingColor: '#000', buttonColor: '#1976D2' },
      category: { expanded: categoryExpanded, toggle: toggleCategoryExpand, input: categoryInput, setInput: (val: string) => { setCategoryInput(val); setCategoriesError(false); }, items: categories, handleAdd: handleAddCategory, handleRemove: handleRemoveCategory, placeholder: "Add categories...", description: "Product category.", error: categoriesError && categories.length === 0 ? 'Required field - please add a category' : '' },
      boundingBox: { expanded: boundingBoxExpanded, toggle: toggleBoundingBoxExpand, input: boundingBoxInput, setInput: setBoundingBoxInput, items: boundingBoxes, handleAdd: handleAddBoundingBox, handleRemove: handleRemoveBoundingBox, placeholder: "Add bounding box coordinates...", description: "Geographic boundaries.", error: boundingBoxError && !boundingBoxCoordinates ? 'Required field - please select a location' : '' }
    };
    return configs[criteriaId];
  };

  // Render criteria block (without individual borders)
  const renderCriteriaBlock = (criteria: CriteriaOption[]) => (
    <Box>
      {criteria.map((item, index) => (
        <Box key={item.id}>
    <Box
      sx={{
              mb: index < criteria.length - 1 ? 2.5 : 0,
              pb: index < criteria.length - 1 ? 2.5 : 0,
              borderBottom: index < criteria.length - 1 ? '1px solid #e0e0e0' : 'none'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedCriteria.includes(item.id)}
                    onChange={() => handleCheckboxChange(item.id)}
                    sx={{
                      color: '#757575',
                      '&.Mui-checked': {
                        color: '#2196F3'
                      }
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: '#000',
                        mb: 0.5,
                        fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 16,
                        color: '#666',
                        fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', m: 0, flex: 1 }}
              />
              
              {/* Expandable indicator - Bright Icon Only (for all criteria) */}
              {selectedCriteria.includes(item.id) && (
                <IconButton
                  size="small"
                  onClick={() => getCriteriaConfig(item.id)?.toggle()}
                  sx={{
                    color: '#1976D2',
                    mt: 0.5,
                    padding: 0.5,
                    '&:hover': {
                      background: 'rgba(25, 118, 210, 0.08)',
                      color: '#1565C0'
                    },
                    transition: 'all 0.2s ease'
                  }}
                  title={getCriteriaConfig(item.id)?.expanded ? "Click to collapse" : "Click to expand"}
                >
                  {getCriteriaConfig(item.id)?.expanded ? (
                    <ExpandLessIcon sx={{ fontSize: 24 }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 24 }} />
                  )}
                </IconButton>
              )}
              {!selectedCriteria.includes(item.id) && (
                <Box sx={{ width: 32 }} /> 
              )}
            </Box>
          </Box>

          {/* Expandable Input Box - Show for any checked criteria when expanded */}
          {selectedCriteria.includes(item.id) && getCriteriaConfig(item.id)?.expanded && (
            <Box sx={{ mt: 2, mb: 3, ml: 5 }}>
              <Paper
                elevation={0}
                sx={{
                  background: '#f8f9fa',
                  border: '1px solid #d0d5dd',
                  borderRadius: 3,
                  p: 2
                }}
              >
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: '#000',
                    mb: 0.5,
                    fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                  }}
                >
                  {item.id === 'boundingBox' ? 'Geographic Bounding Box' : item.id === 'country' ? 'Country Selection' : item.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 15,
                    color: '#666',
                    mb: 1.5,
                    fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                  }}
                >
                  {item.id === 'boundingBox' ? 'Use the map to capture coordinates or manually copy them for your rule.' : getCriteriaConfig(item.id)?.description}
                </Typography>

                {/* Input Row - Special handling for Bounding Box */}
                {item.id === 'boundingBox' ? (
                  // Clickable box to open bounding box dialog
                  <Paper
                    elevation={0}
                    onClick={() => setBoundingBoxDialogOpen(true)}
                    sx={{
                      background: '#fafafa',
                      border: '1px solid #ddd',
                      borderRadius: 2,
                      p: 3,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        background: '#f0f0f0',
                        borderColor: '#bbb',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: '#666',
                        fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                      }}
                    >
                      Enter bounding box coordinates manually
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: '#1976D2',
                        fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
                        mt: 1,
                        fontWeight: 600
                      }}
                    >
                      Click to configure →
                    </Typography>
                  </Paper>
                ) : item.id === 'country' ? (
                  // Multi-select dropdown for countries
                  <Box sx={{ mb: countries.length > 0 ? 1.5 : 0 }}>
                    <FormControl fullWidth error={countriesError && countries.length === 0}>
                      <Select
                        multiple
                        displayEmpty
                        value={countries}
                        onChange={handleCountryChange}
                        input={<OutlinedInput />}
                        error={countriesError && countries.length === 0}
                        renderValue={(selected) => {
                          if (selected.length === 0) {
                            return <em style={{ color: countriesError && countries.length === 0 ? '#d32f2f' : '#999' }}>Select countries</em>;
                          }
                          // Display country names
                          return selected
                            .map((code) => {
                              const country = COUNTRY_OPTIONS.find(c => c.code === code);
                              return country?.name || code;
                            })
                            .join(', ');
                        }}
          sx={{
                          background: '#fff',
                          borderRadius: '8px',
                          '& .MuiSelect-select': {
                            padding: '14px',
                            fontSize: 16,
                            fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                          },
                          ...(countriesError && countries.length === 0 && {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#d32f2f',
                              borderWidth: '2px'
                            }
                          })
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 400
                            }
                          }
                        }}
                      >
                        {COUNTRY_OPTIONS.map((country) => (
                          <MenuItem
                            key={country.code}
                            value={country.code}
            sx={{
                              fontSize: 15,
                              fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                            }}
                          >
                            <Checkbox checked={countries.indexOf(country.code) > -1} />
                            {country.name} ({country.code})
                          </MenuItem>
                        ))}
                      </Select>
                      {countriesError && countries.length === 0 && (
                        <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5 }}>
                          Required field - please select at least one country
                        </Typography>
                      )}
                    </FormControl>
                  </Box>
                ) : item.id === 'brandName' || item.id === 'athleteFullName' ? (
                  // Simple text input for brandName and athleteFullName (single value, not array)
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 0 }}>
                    <TextField
                      fullWidth
                      placeholder={getCriteriaConfig(item.id)?.placeholder?.replace('Add ', 'Enter ').replace('...', '')}
                      value={getCriteriaConfig(item.id)?.input}
                      onChange={(e) => getCriteriaConfig(item.id)?.setInput(e.target.value)}
                      error={!!getCriteriaConfig(item.id)?.error}
                      sx={{
                        '& .MuiInputBase-root': {
                          background: '#fff',
                          borderRadius: '8px'
                        },
                        '& .MuiInputBase-input': {
                          fontSize: 16,
                          fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
                          padding: '14px'
                        }
                      }}
                    />
                    {/* Error message display */}
                    {getCriteriaConfig(item.id)?.error && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#d32f2f', 
                          fontSize: 14,
                          fontWeight: 500,
                          ml: 1
                        }}
                      >
                        {getCriteriaConfig(item.id)?.error}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  // Regular text input with ADD button for other criteria (arrays)
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: getCriteriaConfig(item.id)?.items.length > 0 ? 1.5 : 0 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        fullWidth
                        placeholder={getCriteriaConfig(item.id)?.placeholder}
                        value={getCriteriaConfig(item.id)?.input}
                        onChange={(e) => getCriteriaConfig(item.id)?.setInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            getCriteriaConfig(item.id)?.handleAdd();
                          }
                        }}
                        error={!!getCriteriaConfig(item.id)?.error}
                        sx={{
                          '& .MuiInputBase-root': {
                            background: '#fff',
                            borderRadius: '8px'
                          },
                          '& .MuiInputBase-input': {
                            fontSize: 16,
                            fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
                            padding: '14px'
                          }
                        }}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => getCriteriaConfig(item.id)?.handleAdd()}
                        sx={{
                          color: getCriteriaConfig(item.id)?.buttonColor || '#1976D2',
                          borderColor: getCriteriaConfig(item.id)?.buttonColor || '#1976D2',
                          fontWeight: 600,
                          px: 3,
                          py: 1.5,
                          whiteSpace: 'nowrap',
                          borderRadius: '8px',
                          fontSize: 15,
                          '&:hover': {
                            borderColor: getCriteriaConfig(item.id)?.buttonColor || '#1565C0',
                            background: `rgba(${getCriteriaConfig(item.id)?.buttonColor === '#059669' ? '5, 150, 105' : '25, 118, 210'}, 0.04)`
                          }
                        }}
                      >
                        + ADD
                      </Button>
                    </Box>
                    {/* Error message display */}
                    {getCriteriaConfig(item.id)?.error && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#d32f2f', 
                          fontSize: 14,
                          fontWeight: 500,
                          ml: 1
                        }}
                      >
                        {getCriteriaConfig(item.id)?.error}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Helper Text */}
                {getCriteriaConfig(item.id)?.helperText && (
            <Typography
              sx={{
                      fontSize: 14,
                      color: '#666',
                      mt: 0.5,
                      fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                    }}
                  >
                    {getCriteriaConfig(item.id)?.helperText}
            </Typography>
                )}

                {/* Display Added Items - Skip for brandName and athleteFullName (they're single text inputs) */}
                {getCriteriaConfig(item.id)?.items.length > 0 && item.id !== 'brandName' && item.id !== 'athleteFullName' && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {item.id === 'country' ? (
                      // Display country chips with names
                      countries.map((countryCode: string) => {
                        const country = COUNTRY_OPTIONS.find(c => c.code === countryCode);
                        return (
                          <Chip
                            key={countryCode}
                            label={`${country?.name || countryCode} (${countryCode})`}
                            onDelete={() => handleRemoveCountry(countryCode)}
                            sx={{
                              background: '#e3f2fd',
                              color: '#1976d2',
                              fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                            }}
                          />
                        );
                      })
                    ) : (
                      // Display regular chips for other criteria
                      getCriteriaConfig(item.id)?.items.map((code: string, idx: number) => (
                        <Chip
                          key={idx}
                          label={code}
                          onDelete={() => getCriteriaConfig(item.id)?.handleRemove(idx)}
                          sx={{
                            background: '#e3f2fd',
                            color: '#1976d2',
                            fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                          }}
                        />
                      ))
                    )}
          </Box>
                )}
              </Paper>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );

  return (
            <Box
              sx={{
        minHeight: '100vh',
                background: '#ffffff',
        p: 0
              }}
            >
      <Box sx={{ px: 6, py: 4 }}>
        {/* General Basic Information Section */}
                <Box
                  sx={{
            width: '80%',
            mx: 'auto',
            mb: 6
          }}
        >
          <Typography
            variant="h3"
                        sx={{
              fontFamily: "'Impact', 'Arial Black', sans-serif",
              fontWeight: 700,
              color: '#000',
              fontSize: '28px',
              textTransform: 'uppercase',
              mb: 3,
              letterSpacing: '1px'
            }}
          >
            GENERAL BASIC INFORMATION
          </Typography>

          <Paper
            elevation={0}
            sx={{
              background: '#fafafa',
              border: '1px solid #ddd',
              borderRadius: 2,
              p: 4
            }}
          >
            <Box sx={{ position: 'relative', mb: 3 }}>
              <TextField
                fullWidth
                label="Rule Name"
                placeholder="Enter rule name"
                value={ruleName}
                onChange={(e) => {
                  setRuleName(e.target.value);
                  setRuleNameValid(null);
                  setRuleNameError('');
                }}
                error={!!ruleNameError}
                helperText={ruleNameError}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: 18,
                    fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: 16,
                    fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                      {!editMode && ruleNameValidating && <CircularProgress size={20} />}
                      {!editMode && !ruleNameValidating && ruleNameValid === true && (
                        <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 24 }} />
                      )}
                      {!editMode && !ruleNameValidating && ruleNameValid === false && (
                        <ErrorIcon sx={{ color: '#f44336', fontSize: 24 }} />
                      )}
                    </Box>
                  )
                }}
              />
            </Box>

            <TextField
              fullWidth
              label="Redirect URL"
              placeholder="https://www.nike.com"
              value={redirectUrl}
              onChange={(e) => {
                setRedirectUrl(e.target.value);
                setRedirectUrlError(''); // Clear error when typing
              }}
              onBlur={(e) => validateRedirectUrl(e.target.value)} // Validate when leaving field
              error={!!redirectUrlError}
              helperText={redirectUrlError || 'Enter the full URL starting with https://'}
              sx={{
                mb: 3,
                '& .MuiInputBase-input': {
                  fontSize: 18,
                  fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                },
                '& .MuiInputLabel-root': {
                  fontSize: 16,
                  fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                }
              }}
            />

            <TextField
              fullWidth
              select
              label="Priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
                      sx={{
                '& .MuiInputBase-input': {
                        fontSize: 18,
                  fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                },
                '& .MuiInputLabel-root': {
                  fontSize: 16,
                  fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                }
              }}
            >
              <MenuItem value="" disabled sx={{ 
                fontStyle: 'italic', 
                color: '#666',
                fontSize: 18,
                fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
              }}>
                Select the priority
              </MenuItem>
              <MenuItem value="low" sx={{ 
                fontSize: 18,
                fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
              }}>
                Low
              </MenuItem>
              <MenuItem value="medium" sx={{ 
                fontSize: 18,
                fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
              }}>
                Medium
              </MenuItem>
              <MenuItem value="high" sx={{ 
                fontSize: 18,
                fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
              }}>
                High
              </MenuItem>
              <MenuItem value="critical" sx={{ 
                fontSize: 18,
                fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
              }}>
                Critical
              </MenuItem>
            </TextField>
          </Paper>
        </Box>

        {/* Timing and Schedule Section - Separate Box */}
        <Box
          sx={{
            width: '80%',
            mx: 'auto',
            mb: 6
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Impact', 'Arial Black', sans-serif",
                        fontWeight: 700,
              color: '#000',
              fontSize: '28px',
              textTransform: 'uppercase',
              mb: 3,
              letterSpacing: '1px'
            }}
          >
            TIMING AND SCHEDULE
                    </Typography>

          <Paper
            elevation={0}
            onClick={() => setTimingModalOpen(true)}
            sx={{
              background: '#fafafa',
              border: '1px solid #ddd',
              borderRadius: 2,
              p: 4,
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                background: '#f0f0f0',
                borderColor: '#bbb',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }
            }}
          >
                    <Typography
                      sx={{
                fontSize: 16,
                color: '#666',
                fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
              }}
            >
              Configure the start date/time and expiration date/time for this rule
                    </Typography>
            <Typography
              sx={{
                fontSize: 14,
                color: '#1976D2',
                fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
                mt: 1,
                fontWeight: 600
              }}
            >
              Click to configure →
            </Typography>

            {/* Rule Summary - Shows configured dates (inside same block) */}
            {(startDate || expirationDate) && (
              <Box 
                sx={{ 
                  mt: 2, 
                  pt: 2, 
                  borderTop: '1px solid #e0e0e0'
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#000',
                    mb: 1.5,
                    fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                  }}
                >
                  Rule Summary
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/* Start Date */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Typography sx={{ fontSize: 13, color: '#666', minWidth: '80px', fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif" }}>
                      Start Date:
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: '#000', fontWeight: 500, fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif" }}>
                      {startDate && startTime 
                        ? `${startDate.format('DD/MM/YYYY')}, ${startTime.format('hh:mm A')}` 
                        : startDate 
                          ? startDate.format('DD/MM/YYYY') 
                          : 'Not set'}
                    </Typography>
                  </Box>
                  
                  {/* End Date */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Typography sx={{ fontSize: 13, color: '#666', minWidth: '80px', fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif" }}>
                      End Date:
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: expirationDate ? '#000' : '#ff9800', fontWeight: 500, fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif" }}>
                      {expirationDate && expirationTime
                        ? `${expirationDate.format('DD/MM/YYYY')}, ${expirationTime.format('hh:mm A')}`
                        : expirationDate
                          ? expirationDate.format('DD/MM/YYYY')
                          : 'No expiration'}
                    </Typography>
                  </Box>
                  
                  {/* Duration */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Typography sx={{ fontSize: 13, color: '#666', minWidth: '80px', fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif" }}>
                      Duration:
                    </Typography>
                    <Typography 
                      sx={{ 
                        fontSize: 13, 
                        color: expirationDate ? '#000' : '#ff9800', 
                        fontWeight: 500,
                        fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                      }}
                    >
                      {(() => {
                        if (!startDate) return 'Not set';
                        if (!expirationDate) return 'No expiration';
                        const duration = expirationDate.diff(startDate, 'days');
                        return `${duration} days`;
                      })()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>
                  </Box>

        {/* Page Title */}
        <Box
          sx={{
            width: '80%',
            mx: 'auto',
            mb: 4
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: "'Impact', 'Arial Black', sans-serif",
                fontWeight: 700,
                color: '#000',
                fontSize: '28px',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}
            >
              SELECT MATCHING CRITERIA
            </Typography>
            
            <Tooltip 
              title="Select at least one matching criterion based on what your rule requires"
              placement="right"
              arrow
            >
              <InfoIcon 
                sx={{ 
                  fontSize: '22px', 
                  color: '#666',
                  cursor: 'help',
                  '&:hover': {
                    color: '#2196F3'
                  }
                }} 
              />
            </Tooltip>
          </Box>
                </Box>

        {/* Two-column layout with criteria blocks */}
        <Box
          sx={{
            width: '80%',
            mx: 'auto',
            mb: 4
          }}
        >
          <Paper
            elevation={0}
            sx={{
              background: '#f5f5f5',
              border: '1px solid #ddd',
              borderRadius: 0,
              p: 4
            }}
          >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
              gap: 4,
              alignItems: 'start'
            }}
          >
            {/* Left Block - 4 criteria */}
            <Box>
              {renderCriteriaBlock(leftBlockCriteria)}
            </Box>

            {/* Vertical Divider Line */}
            <Box
              sx={{
                width: '1px',
                background: '#ddd',
                height: '100%',
                minHeight: '400px',
                display: { xs: 'none', md: 'block' }
              }}
            />

            {/* Right Block - 5 criteria */}
            <Box>
              {renderCriteriaBlock(rightBlockCriteria)}
            </Box>
          </Box>
          </Paper>
            </Box>

            {/* Selected Count */}
            {selectedCriteria.length > 0 && (
          <Box sx={{ width: '80%', mx: 'auto', mb: 3, textAlign: 'center' }}>
                <Typography
                  sx={{
                color: '#2196F3',
                fontSize: 16,
                fontWeight: 600,
                fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                  }}
                >
                  {selectedCriteria.length} criteria selected
                </Typography>
              </Box>
            )}

        {/* Navigation Buttons */}
          <Box
            sx={{
            width: '80%',
            mx: 'auto',
              display: 'flex',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            variant="outlined"
            sx={{
              color: '#2196F3',
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              py: 1.5,
              fontSize: 16,
              border: '2px solid #2196F3',
              borderRadius: '8px',
              background: '#fff',
              '&:hover': {
                background: 'rgba(33, 150, 243, 0.08)',
                borderColor: '#1976D2',
                color: '#1976D2'
              }
            }}
          >
            Back
          </Button>

            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              disabled={selectedCriteria.length === 0}
              sx={{
              background: '#2196F3',
                px: 4,
              py: 1.5,
                fontSize: 16,
                fontWeight: 600,
                textTransform: 'none',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                '&:hover': {
                background: '#1976D2',
                boxShadow: '0 6px 16px rgba(33, 150, 243, 0.4)'
                },
                '&.Mui-disabled': {
                background: '#ccc',
                color: '#888'
                }
              }}
            >
              Next
            </Button>
          </Box>
      </Box>

      {/* Timing & Schedule Modal */}
      <Dialog
        open={timingModalOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return;
          setTimingModalOpen(false);
        }}
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '85vh',
            width: '900px'
          }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ p: 3 }}>
              {/* Header Section */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: '#000',
                    mb: 1,
                    fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                  }}
                >
                  Timing & Schedule
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: '#666',
                    fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                  }}
                >
                  Configure when this rule should start and end. These settings stay consistent no matter which matching criteria you choose.
                </Typography>
              </Box>

              {/* No Expiration Checkbox - Above date sections */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasNoExpiration}
                      onChange={(e) => {
                        setHasNoExpiration(e.target.checked);
                        if (e.target.checked) {
                          setExpirationDate(null);
                          setExpirationTime(null);
                        } else {
                          setExpirationDate(dayjs().add(30, 'day'));
                          setExpirationTime(dayjs().hour(18).minute(29));
                        }
                      }}
                      sx={{
                        color: '#2196F3',
                        '&.Mui-checked': {
                          color: '#2196F3',
                        },
                      }}
                    />
                  }
                  label="Rule has no expiration date"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: 14,
                      fontWeight: 500,
                      color: '#333',
                      fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                    }
                  }}
                />
              </Box>

              {/* Two-Column Date/Time Pickers */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                {/* Left Column - Rule Start */}
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    background: '#fafafa',
                    border: '1px solid #ddd',
                    borderRadius: 2,
                    p: 2
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: '#000',
                      mb: 1,
                      fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                    }}
                  >
                    Rule Start Date & Time
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 12, color: '#555', mb: 0.5 }}>Start Date</Typography>
                      <DatePicker
                        value={startDate}
                        onChange={(newValue) => setStartDate(newValue)}
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            sx: { background: '#fff' }
                          },
                          actionBar: {
                            actions: ['clear', 'accept']
                          },
                          desktopPaper: {
                            sx: {
                              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                              '& .MuiPickersCalendarHeader-root': {
                                paddingLeft: 2,
                                paddingRight: 1,
                                '& .MuiPickersCalendarHeader-label': {
                                  fontSize: '18px',
                                  fontWeight: 600,
                                  color: '#000',
                                  fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                                }
                              },
                              '& .MuiDayCalendar-header': {
                                '& .MuiDayCalendar-weekDayLabel': {
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  color: '#000',
                                  fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                                }
                              },
                              '& .MuiPickersDay-root': {
                                fontSize: '14px',
                                fontWeight: 400,
                                color: '#000',
                                fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
                                '&.MuiPickersDay-today': {
                                  border: '1px solid #2196F3'
                                },
                                '&.Mui-selected': {
                                  backgroundColor: '#2196F3 !important',
                                  color: '#fff',
                                  fontWeight: 600,
                                  '&:hover': {
                                    backgroundColor: '#1976D2 !important'
                                  }
                                },
                                '&:hover': {
                                  backgroundColor: 'rgba(33, 150, 243, 0.1)'
                                }
                              },
                              '& .MuiPickersDay-dayOutsideMonth': {
                                color: '#999'
                              },
                              '& .MuiDialogActions-root': {
                                padding: '8px 16px',
                                '& .MuiButton-root': {
                                  fontSize: '14px',
                                  fontWeight: 500,
                                  color: '#2196F3',
                                  textTransform: 'capitalize',
                                  fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                                }
                              }
                            }
                          }
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 12, color: '#555', mb: 0.5 }}>Start Time</Typography>
                      <Box
                        onClick={() => setStartTimeOpen(!startTimeOpen)}
                        sx={{
                          border: '1px solid #ccc',
                          borderRadius: 1,
                          padding: '8px 12px',
                          background: '#fff',
                          cursor: 'pointer',
                          fontSize: 14,
                          '&:hover': {
                            borderColor: '#000'
                          }
                        }}
                      >
                        {startTime ? startTime.format('hh:mm A') : 'Select time'}
                      </Box>
                      {startTimeOpen && (
                        <Paper
                          elevation={3}
                          sx={{
                            position: 'absolute',
                            zIndex: 1300,
                            mt: 1,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                          }}
                        >
                          <MultiSectionDigitalClock
                            value={startTime}
                            onChange={(newValue) => {
                              setStartTime(newValue);
                              setStartTimeOpen(false);
                            }}
                            timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                            skipDisabled={false}
                            sx={{
                              '& .MuiMultiSectionDigitalClockSection-root': {
                                borderRight: 'none !important',
                                border: 'none !important',
                                '&::after': {
                                  display: 'none !important'
                                },
                                '& .MuiMenuItem-root': {
                                  fontSize: '16px',
                                  fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
                                  '&.Mui-selected': {
                                    backgroundColor: '#2196F3',
                                    color: '#fff',
                                    fontWeight: 600
                                  }
                                }
                              },
                              '& .MuiList-root': {
                                border: 'none !important'
                              }
                            }}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, borderTop: '1px solid #e0e0e0' }}>
                            <Button onClick={() => setStartTimeOpen(false)} sx={{ color: '#2196F3', textTransform: 'uppercase' }}>
                              CANCEL
                            </Button>
                            <Button onClick={() => setStartTimeOpen(false)} sx={{ color: '#2196F3', textTransform: 'uppercase' }}>
                              OK
                            </Button>
                          </Box>
                        </Paper>
                      )}
                    </Box>
                  </Box>

                  <Typography sx={{ fontSize: 11, color: '#555', mb: 0.5 }}>
                    Timezone: UTC
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 11,
                      color: '#666',
                      fontStyle: 'italic',
                      fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                    }}
                  >
                    Rule will activate automatically at the start time.
                  </Typography>
                </Paper>

                {/* Right Column - Rule Expiration */}
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    background: hasNoExpiration ? '#f5f5f5' : '#fafafa',
                    border: '1px solid #ddd',
                    borderRadius: 2,
                    p: 2,
                    opacity: hasNoExpiration ? 0.6 : 1,
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: hasNoExpiration ? '#999' : '#000',
                      mb: 1,
                      fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                    }}
                  >
                    Rule Expiration Date & Time
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 12, color: hasNoExpiration ? '#999' : '#555', mb: 0.5 }}>Expiration Date</Typography>
                      <DatePicker
                        value={expirationDate}
                        onChange={(newValue) => setExpirationDate(newValue)}
                        disabled={hasNoExpiration}
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            sx: { background: '#fff' }
                          },
                          actionBar: {
                            actions: ['clear', 'accept']
                          },
                          popper: {
                            sx: {
                              '& .MuiPaper-root': {
                                maxWidth: '280px',
                                width: '280px'
                              }
                            }
                          },
                          desktopPaper: {
                            sx: {
                              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                              maxWidth: '280px !important',
                              width: '280px !important',
                              '& .MuiPickersCalendarHeader-root': {
                                paddingLeft: 1,
                                paddingRight: 1,
                                minHeight: '40px',
                                '& .MuiPickersCalendarHeader-label': {
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  color: '#000',
                                  fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                                }
                              },
                              '& .MuiDayCalendar-root': {
                                width: '100%',
                                maxWidth: '100%'
                              },
                              '& .MuiPickersLayout-root': {
                                maxWidth: '300px'
                              },
                              '& .MuiDayCalendar-header': {
                                justifyContent: 'space-between',
                                '& .MuiDayCalendar-weekDayLabel': {
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  color: '#000',
                                  width: '32px',
                                  height: '32px',
                                  margin: '0 2px',
                                  fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                                }
                              },
                              '& .MuiDayCalendar-slideTransition': {
                                minHeight: '200px'
                              },
                              '& .MuiDayCalendar-weekContainer': {
                                justifyContent: 'space-between',
                                margin: 0
                              },
                              '& .MuiPickersDay-root': {
                                fontSize: '13px',
                                width: '32px',
                                height: '32px',
                                margin: '0 2px',
                                fontWeight: 400,
                                color: '#000',
                                fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
                                '&.MuiPickersDay-today': {
                                  border: '1px solid #2196F3'
                                },
                                '&.Mui-selected': {
                                  backgroundColor: '#2196F3 !important',
                                  color: '#fff',
                                  fontWeight: 600,
                                  '&:hover': {
                                    backgroundColor: '#1976D2 !important'
                                  }
                                },
                                '&:hover': {
                                  backgroundColor: 'rgba(33, 150, 243, 0.1)'
                                }
                              },
                              '& .MuiPickersDay-dayOutsideMonth': {
                                color: '#999'
                              },
                              '& .MuiDialogActions-root': {
                                padding: '8px 16px',
                                '& .MuiButton-root': {
                                  fontSize: '14px',
                                  fontWeight: 500,
                                  color: '#2196F3',
                                  textTransform: 'capitalize',
                                  fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                                }
                              }
                            }
                          }
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 12, color: hasNoExpiration ? '#999' : '#555', mb: 0.5 }}>Expiration Time</Typography>
                      <Box
                        onClick={() => !hasNoExpiration && setExpirationTimeOpen(!expirationTimeOpen)}
                        sx={{
                          border: '1px solid #ccc',
                          borderRadius: 1,
                          padding: '8px 12px',
                          background: hasNoExpiration ? '#f5f5f5' : '#fff',
                          cursor: hasNoExpiration ? 'not-allowed' : 'pointer',
                          fontSize: 14,
                          color: hasNoExpiration ? '#999' : '#000',
                          '&:hover': {
                            borderColor: hasNoExpiration ? '#ccc' : '#000'
                          }
                        }}
                      >
                        {expirationTime ? expirationTime.format('hh:mm A') : 'Select time'}
                      </Box>
                      {expirationTimeOpen && (
                        <Paper
                          elevation={3}
                          sx={{
                            position: 'absolute',
                            zIndex: 1300,
                            mt: 1,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                          }}
                        >
                          <MultiSectionDigitalClock
                            value={expirationTime}
                            onChange={(newValue) => {
                              setExpirationTime(newValue);
                              setExpirationTimeOpen(false);
                            }}
                            timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                            skipDisabled={false}
                            sx={{
                              '& .MuiMultiSectionDigitalClockSection-root': {
                                borderRight: 'none !important',
                                border: 'none !important',
                                '&::after': {
                                  display: 'none !important'
                                },
                                '& .MuiMenuItem-root': {
                                  fontSize: '16px',
                                  fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
                                  '&.Mui-selected': {
                                    backgroundColor: '#2196F3',
                                    color: '#fff',
                                    fontWeight: 600
                                  }
                                }
                              },
                              '& .MuiList-root': {
                                border: 'none !important'
                              }
                            }}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, borderTop: '1px solid #e0e0e0' }}>
                            <Button onClick={() => setExpirationTimeOpen(false)} sx={{ color: '#2196F3', textTransform: 'uppercase' }}>
                              CANCEL
                            </Button>
                            <Button onClick={() => setExpirationTimeOpen(false)} sx={{ color: '#2196F3', textTransform: 'uppercase' }}>
                              OK
                            </Button>
                          </Box>
                        </Paper>
                      )}
                    </Box>
                  </Box>

                  <Typography sx={{ fontSize: 11, color: hasNoExpiration ? '#999' : '#555', mb: 0.5 }}>
                    Timezone: UTC
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 11,
                      color: hasNoExpiration ? '#999' : '#666',
                      fontStyle: 'italic',
                      fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                    }}
                  >
                    {hasNoExpiration 
                      ? 'Rule will remain active indefinitely until manually deactivated.'
                      : 'Rule will deactivate automatically at the expiration time.'}
                  </Typography>
                </Paper>
              </Box>

              {/* Rule Summary Box */}
              <Paper
                elevation={0}
                sx={{
                  background: '#fafafa',
                  border: '1px solid #ddd',
                  borderRadius: 2,
                  p: 2,
                  mb: 2
                }}
              >
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: '#000',
                    mb: 2,
                    fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                  }}
                >
                  Rule Summary
                </Typography>

                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                  <Box>
                    <Typography sx={{ fontSize: 13, color: '#666', mb: 0.5 }}>Rule Duration</Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#000' }}>
                      {calculateDuration()} days
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 13, color: '#666', mb: 0.5 }}>Activation</Typography>
                    <Typography
                      sx={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: getActivationStatus().color
                      }}
                    >
                      {getActivationStatus().text}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 13, color: '#666', mb: 0.5 }}>Will expire</Typography>
                    <Typography
                      sx={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: getExpirationStatus().color
                      }}
                    >
                      {getExpirationStatus().text}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Quick Preset Options */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: '#000',
                    mb: 0.5,
                    fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                  }}
                >
                  Quick Preset Options
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: '#666',
                    mb: 1.5,
                    fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                  }}
                >
                  Choose a preset duration or customize your own timing.
                </Typography>

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {['7 DAYS', '30 DAYS', '90 DAYS', '6 MONTHS', '1 YEAR'].map((preset) => (
                    <Button
                      key={preset}
                      variant={selectedPreset === preset ? 'contained' : 'outlined'}
                      onClick={() => handlePresetClick(preset)}
                      sx={{
                        px: 3,
                        py: 1,
                        fontSize: 13,
                        fontWeight: 700,
                        borderRadius: 6,
                        textTransform: 'uppercase',
                        background: selectedPreset === preset ? '#000' : 'transparent',
                        color: selectedPreset === preset ? '#fff' : '#000',
                        borderColor: '#000',
                        '&:hover': {
                          background: selectedPreset === preset ? '#333' : 'rgba(0, 0, 0, 0.05)',
                          borderColor: '#000'
                        }
                      }}
                    >
                      {preset}
                    </Button>
                  ))}
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => setTimingModalOpen(false)}
                  sx={{
                    px: 3,
                    py: 1,
                    fontSize: 14,
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    borderColor: '#ddd',
                    color: '#666',
                    '&:hover': {
                      borderColor: '#999',
                      background: '#f5f5f5'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setTimingModalOpen(false)}
                  sx={{
                    px: 3,
                    py: 1,
                    fontSize: 14,
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    background: '#000',
                    '&:hover': {
                      background: '#333'
                    }
                  }}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </LocalizationProvider>
        </DialogContent>
      </Dialog>

      {/* Bounding Box Configuration Modal */}
      <Dialog
        open={boundingBoxDialogOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return;
          setBoundingBoxDialogOpen(false);
        }}
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            width: '700px'
          }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: '#000',
                  mb: 1,
                  fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                }}
              >
                Bounding Box Configuration
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  color: '#666',
                  fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                }}
              >
                Enter the geographic coordinates to define the bounding box for this rule.
              </Typography>
            </Box>

            {/* Coordinate Input Fields */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="North Latitude"
                  type="number"
                  inputProps={{ step: "0.000001", min: "-90", max: "90" }}
                  InputLabelProps={{ shrink: true }}
                  value={boundingBoxCoordinates?.north === 0 ? '' : boundingBoxCoordinates?.north ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                    setBoundingBoxCoordinates(prev => ({
                      north: isNaN(value) ? 0 : value,
                      south: prev?.south ?? 0,
                      east: prev?.east ?? 0,
                      west: prev?.west ?? 0
                    }));
                    setBoundingBoxError(false);
                  }}
                  error={boundingBoxError && !boundingBoxCoordinates}
                  helperText="Enter latitude for northern boundary (-90 to 90)"
                  placeholder="e.g., 45.5231"
                  sx={{
                    '& input[type=number]': {
                      MozAppearance: 'textfield'
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0
                    }
                  }}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="South Latitude"
                  type="number"
                  inputProps={{ step: "0.000001", min: "-90", max: "90" }}
                  InputLabelProps={{ shrink: true }}
                  value={boundingBoxCoordinates?.south === 0 ? '' : boundingBoxCoordinates?.south ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                    setBoundingBoxCoordinates(prev => ({
                      north: prev?.north ?? 0,
                      south: isNaN(value) ? 0 : value,
                      east: prev?.east ?? 0,
                      west: prev?.west ?? 0
                    }));
                    setBoundingBoxError(false);
                  }}
                  error={boundingBoxError && !boundingBoxCoordinates}
                  helperText="Enter latitude for southern boundary (-90 to 90)"
                  placeholder="e.g., 45.5100"
                  sx={{
                    '& input[type=number]': {
                      MozAppearance: 'textfield'
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0
                    }
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="East Longitude"
                  type="number"
                  inputProps={{ step: "0.000001", min: "-180", max: "180" }}
                  InputLabelProps={{ shrink: true }}
                  value={boundingBoxCoordinates?.east === 0 ? '' : boundingBoxCoordinates?.east ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                    setBoundingBoxCoordinates(prev => ({
                      north: prev?.north ?? 0,
                      south: prev?.south ?? 0,
                      east: isNaN(value) ? 0 : value,
                      west: prev?.west ?? 0
                    }));
                    setBoundingBoxError(false);
                  }}
                  error={boundingBoxError && !boundingBoxCoordinates}
                  helperText="Enter longitude for eastern boundary (-180 to 180)"
                  placeholder="e.g., -122.6750"
                  sx={{
                    '& input[type=number]': {
                      MozAppearance: 'textfield'
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0
                    }
                  }}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="West Longitude"
                  type="number"
                  inputProps={{ step: "0.000001", min: "-180", max: "180" }}
                  InputLabelProps={{ shrink: true }}
                  value={boundingBoxCoordinates?.west === 0 ? '' : boundingBoxCoordinates?.west ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                    setBoundingBoxCoordinates(prev => ({
                      north: prev?.north ?? 0,
                      south: prev?.south ?? 0,
                      east: prev?.east ?? 0,
                      west: isNaN(value) ? 0 : value
                    }));
                    setBoundingBoxError(false);
                  }}
                  error={boundingBoxError && !boundingBoxCoordinates}
                  helperText="Enter longitude for western boundary (-180 to 180)"
                  placeholder="e.g., -122.6900"
                  sx={{
                    '& input[type=number]': {
                      MozAppearance: 'textfield'
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Button
                variant="outlined"
                onClick={() => setBoundingBoxDialogOpen(false)}
                sx={{
                  px: 3,
                  py: 1,
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#666',
                  borderColor: '#ddd',
                  textTransform: 'none',
                  fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
                  '&:hover': {
                    borderColor: '#bbb',
                    background: '#f5f5f5'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => setBoundingBoxDialogOpen(false)}
                sx={{
                  px: 3,
                  py: 1,
                  fontSize: 14,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
                  background: '#1976D2',
                  '&:hover': {
                    background: '#1565C0'
                  }
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Unsaved Changes Warning Dialog */}
      <Dialog
        open={unsavedChangesDialogOpen}
        onClose={() => {
          setUnsavedChangesDialogOpen(false);
          setPendingNavigation(null);
        }}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningAmberIcon sx={{ color: '#ff9800', fontSize: 28 }} />
            <Typography variant="h6" fontWeight={700}>
              Unsaved Changes
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. If you leave now, your changes will be lost.
            Are you sure you want to leave this page?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setUnsavedChangesDialogOpen(false);
              setPendingNavigation(null);
            }}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#ddd',
              color: '#666',
              '&:hover': {
                borderColor: '#bbb',
                background: '#f5f5f5'
              }
            }}
          >
            Stay on Page
          </Button>
          <Button
            onClick={() => {
              setUnsavedChangesDialogOpen(false);
              setHasUnsavedChanges(false);
              setIsSubmitting(true);
              if (pendingNavigation) {
                navigate(pendingNavigation, { state: { view: 'my-rules' } });
              }
              setPendingNavigation(null);
            }}
            variant="contained"
            color="error"
            sx={{
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Leave Without Saving
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Notification */}
      <ErrorNotification
        open={showErrorNotification}
        message={errorNotifMessage}
        onClose={() => setShowErrorNotification(false)}
      />
    </Box>
  );
};

export default CreateRule;
